import axios from 'axios';

/**
 * The doctor's Paziresh24 chat («گفت‌وگو») settings. The chat service answers with the session cookie,
 * the same way the unread badge in the bottom navigation does. 403 = this account has no chat settings.
 */
const chatClient = axios.create({
  baseURL: 'https://hami-back.paziresh24.com/api/external',
  withCredentials: true,
  timeout: 8000,
});

const unwrap = <T>(body: any): T => (body?.data ?? body) as T;

export const isForbidden = (error: unknown) => (error as any)?.response?.status === 403;

/* ---- گفت‌وگوی سریع / دستیار بالینی / تماس صوتی ---- */

export const quickChatKey = ['chat-settings', 'quick-chat'] as const;
export const getQuickChat = async (): Promise<boolean> => {
  const { data } = await chatClient.get('/quick-chat/status');
  return !!(typeof data?.data?.status === 'boolean' ? data.data.status : data?.status);
};
export const setQuickChat = async (status: boolean): Promise<boolean> => {
  const { data } = await chatClient.put('/quick-chat/status', { status });
  const saved = typeof data?.data?.status === 'boolean' ? data.data.status : data?.status;
  return typeof saved === 'boolean' ? saved : status;
};

export type AutoReplySettings = {
  conversation_start_enabled?: boolean;
  conversation_start_message?: string | null;
  no_response_enabled?: boolean;
  no_response_minutes?: number | null;
  no_response_message?: string | null;
  smart_intake_enabled?: boolean;
  clinical_copilot_enabled?: boolean;
};
export const autoReplyKey = ['chat-settings', 'auto-reply'] as const;
export const getAutoReply = async () => unwrap<AutoReplySettings>((await chatClient.get('/auto-reply-settings')).data);
export const updateAutoReply = async (payload: AutoReplySettings) =>
  unwrap<AutoReplySettings>((await chatClient.put('/auto-reply-settings', payload)).data);
export const setClinicalCopilot = async (enabled: boolean) =>
  unwrap<AutoReplySettings>((await chatClient.patch('/auto-reply-settings/clinical-copilot', { enabled })).data);

export const voiceCallKey = ['chat-settings', 'voice-call'] as const;
export const getVoiceCall = async (): Promise<boolean | null> => {
  const data = unwrap<{ patient_can_call?: boolean }>((await chatClient.get('/voice-call/settings')).data);
  return typeof data?.patient_can_call === 'boolean' ? data.patient_can_call : null;
};
export const setVoiceCall = async (patientCanCall: boolean) => {
  const data = unwrap<{ patient_can_call?: boolean }>((await chatClient.put('/voice-call/settings', { patient_can_call: patientCanCall })).data);
  return typeof data?.patient_can_call === 'boolean' ? data.patient_can_call : patientCanCall;
};

/* ---- بستن گفت‌وگو ---- */

export type ClosedChatMode = 'DIRECT_REPLY' | 'REQUEST_ONLY' | 'READ_ONLY';
export type LifecycleValues = {
  auto_close_hours?: number | null;
  auto_close_idle_hours?: number | null;
  closed_chat_mode?: ClosedChatMode | null;
  closed_reply_days_limit?: number | null;
  require_reopen_reason?: boolean | null;
  allow_reopen_after_rejection?: boolean | null;
};
export type LifecycleBundle = { defaults?: LifecycleValues; global?: LifecycleValues };
export const lifecycleKey = ['chat-settings', 'lifecycle'] as const;
export const getLifecycle = async () => unwrap<LifecycleBundle>((await chatClient.get('/chat-lifecycle-settings')).data);
export const updateLifecycle = async (payload: LifecycleValues) =>
  unwrap<LifecycleBundle>((await chatClient.put('/chat-lifecycle-settings', payload)).data);

/**
 * Nelson tells GrowthBook which doctors changed these rules (saved group `online-visit-chat-lifecycle-doctor-slugs`)
 * right after a successful save; best effort, a failure never blocks the save.
 */
export const registerLifecycleDoctorSlug = async (slug: string) => {
  if (!slug.trim()) return;
  await axios.post(
    'https://apigw.paziresh24.com/v1/n8n-nelson/webhook/online-visit-chat-lifecycle-doctor-slug',
    { slug: slug.trim() },
    { withCredentials: true },
  );
};

/* ---- پیامک گفت‌وگو ---- */

export const chatSmsKey = ['chat-settings', 'sms'] as const;
export const getChatSms = async (): Promise<boolean> => {
  const data = unwrap<{ sms_notifications_enabled?: boolean }>((await chatClient.get('/users/me/notification-settings')).data);
  return data?.sms_notifications_enabled !== false;
};
export const setChatSms = async (enabled: boolean): Promise<boolean> => {
  const data = unwrap<{ sms_notifications_enabled?: boolean }>(
    (await chatClient.put('/users/me/notification-settings', { sms_notifications_enabled: enabled })).data,
  );
  return data?.sms_notifications_enabled !== false;
};

/* ---- تأیید دومرحله‌ای ---- */

export const twoFaKey = ['chat-settings', 'two-fa'] as const;
export const getTwoFa = async (): Promise<boolean> => !!(await chatClient.get('/users/me/two-fa/status')).data?.enabled;
export const setupTwoFa = async (): Promise<{ qrDataUrl: string; secretBase32: string }> =>
  (await chatClient.post('/users/me/two-fa/setup')).data;
/** Codes typed on a Persian/Arabic keyboard (۱۲۳ / ١٢٣) become Latin digits before they are sent. */
const cleanCode = (code: string) =>
  code
    .replace(/[۰-۹]/g, d => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, d => String(d.charCodeAt(0) - 0x0660))
    .replace(/\D/g, '');
export const verifyTwoFaSetup = async (code: string) => (await chatClient.post('/users/me/two-fa/verify-setup', { code: cleanCode(code) })).data;
export const disableTwoFa = async (code: string) => (await chatClient.post('/users/me/two-fa/disable', { code: cleanCode(code) })).data;

export const apiMessage = (error: unknown, fallback: string) => {
  const message = (error as any)?.response?.data?.message;
  return typeof message === 'string' && /[؀-ۿ]/.test(message) ? message : fallback;
};

/* ---- دستیار هوشمند (smart intake) ---- */

export type IntakePlaybook = {
  version?: number;
  template_id?: string;
  title?: string;
  tone?: string;
  max_questions?: number;
  topic_ids?: string[];
  custom_notes?: string;
  completion_message?: string;
  language?: string;
};
export type IntakeSuggestions = { summary_reasoning?: string; suggested_topic_ids?: string[]; suggested_custom_notes?: string[] };

export const getAutoReplyFull = async () =>
  unwrap<AutoReplySettings & { smart_intake_playbook?: IntakePlaybook | null }>((await chatClient.get('/auto-reply-settings')).data);
export const saveSmartIntake = async (enabled: boolean, playbook: IntakePlaybook) =>
  unwrap<AutoReplySettings>((await chatClient.put('/auto-reply-settings', { smart_intake_enabled: enabled, smart_intake_playbook: playbook })).data);
/** Reads the doctor's recent chats and proposes the playbook; the service allows it once per 10 minutes. */
export const getIntakeSuggestions = async () =>
  unwrap<IntakeSuggestions>((await chatClient.post('/smart-intake/playbook-suggestions', { force: true }, { timeout: 60000 })).data);
export const isRateLimited = (error: unknown) => (error as any)?.response?.status === 429 || (error as any)?.response?.data?.code === 'SUGGESTIONS_RATE_LIMIT';

/* ---- پاسخ‌های سریع ---- */

export type QuickReply = { id: number; shortcut: string; content: string; usage_count?: number };
export const quickRepliesKey = ['chat-settings', 'quick-replies'] as const;
export const getQuickReplies = async (): Promise<QuickReply[]> => {
  const list = (await chatClient.get('/quick-replies')).data?.data;
  return Array.isArray(list) ? list : [];
};
export const createQuickReply = async (input: { shortcut: string; content: string }) =>
  unwrap<QuickReply>((await chatClient.post('/quick-replies', input)).data);
export const updateQuickReply = async (id: number, input: { shortcut: string; content: string }) =>
  unwrap<QuickReply>((await chatClient.put(`/quick-replies/${id}`, input)).data);
export const deleteQuickReply = async (id: number) => {
  await chatClient.delete(`/quick-replies/${id}`);
};
